"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NftPicker } from "@/components/nft-picker/NftPicker";
import type { NftPickerChange } from "@/components/nft-picker/NftPicker.types";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { NFTSearchResult } from "@/components/header/header-search/HeaderSearchModalItem";
import { MEMES_CONTRACT } from "@/constants/constants";
import type { ApiWaveCreditNft } from "@/generated/models/ApiWaveCreditNft";
import { commonApiFetch } from "@/services/api/common-api";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
const MEMES_CONTRACT_LOWER = MEMES_CONTRACT.toLowerCase();
const VALIDATION_MESSAGES: Partial<Record<CREATE_WAVE_VALIDATION_ERROR, string>> = {
  [CREATE_WAVE_VALIDATION_ERROR.CARD_SET_TDH_VOTING_NFTS_REQUIRED]: "Choose at least one Meme card.",
  [CREATE_WAVE_VALIDATION_ERROR.CARD_SET_TDH_VOTING_NFTS_CONTRACT_INVALID]: "Only Meme cards can be used for Meme Card TDH.",
  [CREATE_WAVE_VALIDATION_ERROR.CARD_SET_TDH_VOTING_MEME_COUNT_UNAVAILABLE]: "Meme card count is not loaded yet.",
  [CREATE_WAVE_VALIDATION_ERROR.CARD_SET_TDH_VOTING_FULL_SET_NOT_ALLOWED]: "Selecting all Meme cards is the same as normal TDH. Choose a smaller set."
};
const FULL_SET_MESSAGE = "Selecting all Meme cards is the same as normal TDH. Choose a smaller set.";
const MAX_SELECTED_MESSAGE = "Leave at least one Meme card unselected.";
const NON_MEME_MESSAGE = "Only Meme cards can be added.";
const INVALID_MEME_ID_MESSAGE = "Only existing Meme card IDs can be added.";
const isMemeContract = (contract: string): boolean => contract.toLowerCase() === MEMES_CONTRACT_LOWER;
const getNormalizedMemeTokenIds = (tokenIds: readonly number[]): number[] => Array.from(new Set(tokenIds.filter(tokenId => Number.isInteger(tokenId) && tokenId > 0))).toSorted((left, right) => left - right);
const toMemeCreditNfts = (tokenIds: readonly number[]): ApiWaveCreditNft[] => {
  const uniqueIds = getNormalizedMemeTokenIds(tokenIds);
  return uniqueIds.map(tokenId => ({
    contract: MEMES_CONTRACT,
    token_id: tokenId
  }));
};
const getCreditNftTokenIds = (creditNfts: readonly ApiWaveCreditNft[]): number[] => Array.from(new Set(creditNfts.filter(nft => isMemeContract(nft.contract)).map(nft => nft.token_id).filter(tokenId => Number.isInteger(tokenId) && tokenId > 0))).toSorted((left, right) => left - right);
const isFullMemeSetSelection = ({
  selectedCount,
  memeCount
}: {
  readonly selectedCount: number;
  readonly memeCount: number | null;
}): boolean => memeCount !== null && selectedCount >= memeCount;
const useMemeCardSearch = searchCriteria => {
  const $ = _c(11);
  let t0;
  if ($[0] !== searchCriteria) {
    t0 = searchCriteria.trim();
    $[0] = searchCriteria;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const trimmedSearchCriteria = t0;
  let t1;
  if ($[2] !== trimmedSearchCriteria) {
    t1 = trimmedSearchCriteria.length > 0 && !Number.isNaN(Number(trimmedSearchCriteria));
    $[2] = trimmedSearchCriteria;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isNumericSearch = t1;
  const enabled = trimmedSearchCriteria.length >= 3 || isNumericSearch;
  let t2;
  let t3;
  if ($[4] !== trimmedSearchCriteria) {
    t2 = [QueryKey.NFTS_SEARCH, {
      scope: "meme-card-set",
      search: trimmedSearchCriteria
    }];
    t3 = async () => await commonApiFetch({
      endpoint: "nfts_search",
      params: {
        search: trimmedSearchCriteria
      }
    });
    $[4] = trimmedSearchCriteria;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== enabled || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      enabled,
      staleTime: 60000
    };
    $[7] = enabled;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return useQuery(t4);
};
function MemeCardSearch(t0) {
  const $ = _c(30);
  const {
    selectedIds,
    memeCount,
    onAdd,
    onReject
  } = t0;
  const [searchCriteria, setSearchCriteria] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const {
    data,
    isFetching
  } = useMemeCardSearch(searchCriteria);
  let t1;
  if ($[0] !== data) {
    t1 = data ?? [];
    $[0] = data;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = t1.filter(_temp);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const memeResults = t2;
  let t3;
  if ($[4] !== memeCount || $[5] !== onAdd || $[6] !== onReject || $[7] !== selectedIds) {
    t3 = item_0 => {
      if (!isMemeContract(item_0.contract)) {
        onReject(NON_MEME_MESSAGE);
        return;
      }
      if (isFullMemeSetSelection({
        selectedCount: selectedIds.has(item_0.id) ? selectedIds.size : selectedIds.size + 1,
        memeCount
      })) {
        onReject(FULL_SET_MESSAGE);
        return;
      }
      onAdd(item_0.id);
      setSearchCriteria("");
      setIsOpen(false);
    };
    $[4] = memeCount;
    $[5] = onAdd;
    $[6] = onReject;
    $[7] = selectedIds;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleSelect = t3;
  let searchResultItems;
  if (isFetching) {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <li className="tw-px-3 tw-py-2 tw-text-sm tw-text-iron-300">Loading...</li>;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    searchResultItems = t4;
  } else {
    if (memeResults.length > 0) {
      let t4;
      if ($[10] !== handleSelect || $[11] !== memeResults || $[12] !== selectedIds) {
        let t5;
        if ($[14] !== handleSelect || $[15] !== selectedIds) {
          t5 = item_1 => {
            const isSelected = selectedIds.has(item_1.id);
            return <li key={`${item_1.contract}-${item_1.id}`}><button type="button" onClick={() => handleSelect(item_1)} className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-rounded-md tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-iron-100 hover:tw-bg-iron-800 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-400" aria-label={`Add The Memes #${item_1.id}`}><span className="tw-truncate">#{item_1.id} - {item_1.name}</span>{isSelected && <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-text-primary-300">Selected</span>}</button></li>;
          };
          $[14] = handleSelect;
          $[15] = selectedIds;
          $[16] = t5;
        } else {
          t5 = $[16];
        }
        t4 = memeResults.map(t5);
        $[10] = handleSelect;
        $[11] = memeResults;
        $[12] = selectedIds;
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      searchResultItems = t4;
    } else {
      let t4;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <li className="tw-px-3 tw-py-2 tw-text-sm tw-text-iron-300">No Meme card results</li>;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      searchResultItems = t4;
    }
  }
  let t4;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <label htmlFor="meme-card-set-search" className="tw-text-sm tw-font-semibold tw-text-iron-100">Add by search</label>;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event => {
      setSearchCriteria(event.target.value);
      setIsOpen(true);
    };
    t6 = () => setIsOpen(true);
    $[19] = t5;
    $[20] = t6;
  } else {
    t5 = $[19];
    t6 = $[20];
  }
  let t7;
  if ($[21] !== searchCriteria) {
    t7 = <input id="meme-card-set-search" type="text" value={searchCriteria} onChange={t5} onFocus={t6} placeholder="Search Meme card name or ID" className="tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-black tw-px-3 tw-py-2.5 tw-text-sm tw-text-iron-200 placeholder:tw-text-iron-600 focus:tw-border-primary-400 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-400" />;
    $[21] = searchCriteria;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== isOpen || $[24] !== searchCriteria || $[25] !== searchResultItems) {
    t8 = isOpen && searchCriteria.trim().length > 0 && <div className="tw-absolute tw-left-0 tw-right-0 tw-top-full tw-z-20 tw-mt-2 tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-iron-900 tw-shadow-xl"><ul className="tw-m-0 tw-max-h-56 tw-list-none tw-overflow-y-auto tw-p-1">{searchResultItems}</ul></div>;
    $[23] = isOpen;
    $[24] = searchCriteria;
    $[25] = searchResultItems;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t7 || $[28] !== t8) {
    t9 = <div className="tw-relative tw-flex tw-flex-col tw-gap-2">{t4}{t7}{t8}</div>;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
function _temp(item) {
  return isMemeContract(item.contract);
}
export default function MemeCardSetPicker(t0) {
  const $ = _c(68);
  const {
    creditNfts,
    memeCount,
    isMemeCountLoading,
    isMemeCountError,
    errors,
    onCreditNftsChange
  } = t0;
  const [localError, setLocalError] = useState(null);
  const [pickerSyncVersion, setPickerSyncVersion] = useState(0);
  let t1;
  if ($[0] !== creditNfts) {
    t1 = getCreditNftTokenIds(creditNfts);
    $[0] = creditNfts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedTokenIds = t1;
  let t2;
  if ($[2] !== selectedTokenIds) {
    t2 = new Set(selectedTokenIds);
    $[2] = selectedTokenIds;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectedIdSet = t2;
  let t3;
  if ($[4] !== pickerSyncVersion || $[5] !== selectedTokenIds) {
    t3 = {
      selectedTokenIds,
      syncVersion: pickerSyncVersion
    };
    $[4] = pickerSyncVersion;
    $[5] = selectedTokenIds;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const pickerValueInput = t3;
  let t4;
  if ($[7] !== pickerValueInput.selectedTokenIds) {
    t4 = pickerValueInput.selectedTokenIds.map(_temp2);
    $[7] = pickerValueInput.selectedTokenIds;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = {
      chain: "ethereum" as const,
      contractAddress: MEMES_CONTRACT as `0x${string}`,
      selectedIds: t4,
      allSelected: false
    };
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const pickerValue = t5;
  let t6;
  if ($[11] !== memeCount) {
    t6 = typeof memeCount === "number" && Number.isFinite(memeCount) ? String(memeCount) : undefined;
    $[11] = memeCount;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = {
      address: MEMES_CONTRACT as `0x${string}`,
      name: "The Memes",
      symbol: "MEMES",
      tokenType: "ERC1155" as const,
      totalSupply: t6,
      floorPriceEth: null,
      imageUrl: null,
      isSpam: false
    };
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const fixedContract = t7;
  let t8;
  if ($[15] !== memeCount) {
    t8 = typeof memeCount === "number" && Number.isFinite(memeCount) ? Math.max(0, memeCount - 1) : undefined;
    $[15] = memeCount;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const maxSelectedCount = t8;
  let t9;
  if ($[17] !== memeCount) {
    t9 = typeof memeCount === "number" && Number.isInteger(memeCount) && memeCount > 0;
    $[17] = memeCount;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const hasValidMemeCount = t9;
  let t10;
  if ($[19] !== errors || $[20] !== hasValidMemeCount || $[21] !== localError) {
    let t11;
    if ($[23] !== hasValidMemeCount) {
      t11 = error => error !== CREATE_WAVE_VALIDATION_ERROR.CARD_SET_TDH_VOTING_MEME_COUNT_UNAVAILABLE || !hasValidMemeCount;
      $[23] = hasValidMemeCount;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    const validationMessages = errors.filter(t11).map(_temp3).filter(_temp4);
    const t12 = [localError, ...validationMessages];
    let t13;
    if ($[25] !== t12) {
      t13 = new Set(t12.filter(_temp5));
      $[25] = t12;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    t10 = Array.from(t13);
    $[19] = errors;
    $[20] = hasValidMemeCount;
    $[21] = localError;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const displayedMessages = t10;
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = message_1 => {
      setLocalError(message_1);
      setPickerSyncVersion(_temp6);
    };
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const rejectPickerChange = t11;
  let t12;
  if ($[28] !== memeCount || $[29] !== onCreditNftsChange) {
    t12 = tokenIds => {
      const nextCreditNfts = toMemeCreditNfts(tokenIds);
      if (isFullMemeSetSelection({
        selectedCount: nextCreditNfts.length,
        memeCount
      })) {
        rejectPickerChange(FULL_SET_MESSAGE);
        return;
      }
      setLocalError(null);
      onCreditNftsChange(nextCreditNfts);
    };
    $[28] = memeCount;
    $[29] = onCreditNftsChange;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const applyTokenIds = t12;
  let t13;
  if ($[31] !== hasValidMemeCount || $[32] !== memeCount) {
    t13 = tokenIds_0 => {
      const hasInvalidTokenId = tokenIds_0.some(_temp7);
      if (hasInvalidTokenId) {
        rejectPickerChange(INVALID_MEME_ID_MESSAGE);
        return false;
      }
      const normalizedTokenIds = getNormalizedMemeTokenIds(tokenIds_0);
      if (normalizedTokenIds.length === 0) {
        return true;
      }
      const highestExistingMemeCardId = hasValidMemeCount ? memeCount : null;
      if (highestExistingMemeCardId === null) {
        rejectPickerChange(INVALID_MEME_ID_MESSAGE);
        return false;
      }
      if (normalizedTokenIds.some(tokenId_1 => tokenId_1 > highestExistingMemeCardId)) {
        rejectPickerChange(INVALID_MEME_ID_MESSAGE);
        return false;
      }
      return true;
    };
    $[31] = hasValidMemeCount;
    $[32] = memeCount;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const validateTypedTokenIds = t13;
  let t14;
  if ($[34] !== applyTokenIds || $[35] !== validateTypedTokenIds) {
    t14 = change => {
      if (!change) {
        applyTokenIds([]);
        return;
      }
      if ("type" in change) {
        rejectPickerChange(change.error);
        return;
      }
      if (!isMemeContract(change.contractAddress)) {
        rejectPickerChange(NON_MEME_MESSAGE);
        return;
      }
      if (change.allSelected === true) {
        rejectPickerChange(FULL_SET_MESSAGE);
        return;
      }
      const tokenIds_1 = change.outputMode === "number" ? change.tokenIds : change.tokenIds.map(_temp8);
      if (!validateTypedTokenIds(tokenIds_1)) {
        return;
      }
      applyTokenIds(tokenIds_1);
    };
    $[34] = applyTokenIds;
    $[35] = validateTypedTokenIds;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const handlePickerChange = t14;
  let t15;
  if ($[37] !== applyTokenIds || $[38] !== selectedTokenIds) {
    t15 = tokenId_3 => {
      applyTokenIds([...selectedTokenIds, tokenId_3]);
    };
    $[37] = applyTokenIds;
    $[38] = selectedTokenIds;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const addTokenId = t15;
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div><p className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-50">Meme Card TDH</p><p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-iron-400">Select specific Meme cards. Choosing every Meme card is normal TDH.</p></div>;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== isMemeCountLoading) {
    t17 = isMemeCountLoading && <div className="tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-iron-900 tw-p-3 tw-text-sm tw-text-iron-300">Loading Meme card count...</div>;
    $[41] = isMemeCountLoading;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== isMemeCountError) {
    t18 = isMemeCountError && <div className="tw-rounded-lg tw-border tw-border-solid tw-border-error/40 tw-bg-error/10 tw-p-3 tw-text-sm tw-text-error">Unable to load Meme card count. You cannot continue until it loads.</div>;
    $[43] = isMemeCountError;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== addTokenId || $[46] !== memeCount || $[47] !== selectedIdSet) {
    t19 = <MemeCardSearch selectedIds={selectedIdSet} memeCount={memeCount} onAdd={addTokenId} onReject={setLocalError} />;
    $[45] = addTokenId;
    $[46] = memeCount;
    $[47] = selectedIdSet;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== fixedContract || $[50] !== handlePickerChange || $[51] !== maxSelectedCount || $[52] !== pickerValue) {
    t20 = <NftPicker value={pickerValue} onChange={handlePickerChange} fixedContract={fixedContract} allowAll={false} allowRanges={true} maxSelectedCount={maxSelectedCount} maxSelectedCountMessage={MAX_SELECTED_MESSAGE} placeholder="The Memes" variant="flat" />;
    $[49] = fixedContract;
    $[50] = handlePickerChange;
    $[51] = maxSelectedCount;
    $[52] = pickerValue;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== selectedTokenIds.length) {
    t21 = selectedTokenIds.length.toLocaleString();
    $[54] = selectedTokenIds.length;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  const t22 = selectedTokenIds.length === 1 ? "" : "s";
  let t23;
  if ($[56] !== t21 || $[57] !== t22) {
    t23 = <div className="tw-text-sm tw-text-iron-300">{t21} Meme card{t22} selected</div>;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== displayedMessages) {
    t24 = displayedMessages.map(_temp9);
    $[59] = displayedMessages;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t17 || $[62] !== t18 || $[63] !== t19 || $[64] !== t20 || $[65] !== t23 || $[66] !== t24) {
    t25 = <section className="tw-col-span-full tw-mt-2 tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-p-4">{t16}{t17}{t18}{t19}{t20}{t23}{t24}</section>;
    $[61] = t17;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t23;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  return t25;
}
function _temp9(message_2) {
  return <div key={message_2} className="tw-rounded-lg tw-border tw-border-solid tw-border-error/40 tw-bg-error/10 tw-p-3 tw-text-sm tw-text-error">{message_2}</div>;
}
function _temp8(tokenId_2) {
  return Number(tokenId_2);
}
function _temp7(tokenId_0) {
  return !Number.isInteger(tokenId_0) || tokenId_0 <= 0;
}
function _temp6(version) {
  return version + 1;
}
function _temp5(message_0) {
  return !!message_0;
}
function _temp4(message) {
  return !!message;
}
function _temp3(error_0) {
  return VALIDATION_MESSAGES[error_0];
}
function _temp2(tokenId) {
  return BigInt(tokenId);
}